import { AssetError, DEFAULT_ASSET_LIMITS } from '@dg-embalajes/assets';

export interface NormalizedImportedFile {
  readonly file: File;
  readonly kind: 'image' | 'pdf';
  readonly convertedFromHeic: boolean;
}

const HEADER_BYTES = 2 * 1024 * 1024;
const HEIC_EXTENSION = /\.hei[cf]$/iu;
const HEIC_MIME = /^image\/hei[cf]$/iu;
const HEIC_BRANDS = new Set(['heic', 'heix', 'hevc', 'hevx', 'heim', 'heis']);
const GENERIC_HEIF_BRANDS = new Set(['mif1', 'msf1']);
const AVIF_BRANDS = new Set(['avif', 'avis']);

function ascii(bytes: Uint8Array, offset: number, length: number): string {
  return String.fromCharCode(...bytes.subarray(offset, offset + length));
}

function uint32(bytes: Uint8Array, offset: number): number {
  return new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength).getUint32(offset);
}

function findType(bytes: Uint8Array, type: string, start = 0): number {
  const first = type.charCodeAt(0);
  for (let offset = start; offset <= bytes.length - type.length; offset += 1) {
    if (bytes[offset] !== first) continue;
    if (ascii(bytes, offset, type.length) === type) return offset;
  }
  return -1;
}

function ftypBrands(bytes: Uint8Array): ReadonlySet<string> {
  const typeOffset = findType(bytes.subarray(0, Math.min(bytes.length, 256)), 'ftyp');
  if (typeOffset < 4 || typeOffset + 8 > bytes.length) return new Set();
  const boxStart = typeOffset - 4;
  const boxSize = uint32(bytes, boxStart);
  if (boxSize < 16 || boxStart + boxSize > bytes.length) return new Set();
  const brands = new Set<string>([ascii(bytes, typeOffset + 4, 4)]);
  for (let offset = typeOffset + 12; offset + 4 <= boxStart + boxSize; offset += 4) {
    brands.add(ascii(bytes, offset, 4));
  }
  return brands;
}

function isHeic(bytes: Uint8Array, hinted: boolean): boolean {
  const brands = ftypBrands(bytes);
  if ([...brands].some((brand) => AVIF_BRANDS.has(brand))) return false;
  if ([...brands].some((brand) => HEIC_BRANDS.has(brand))) return true;
  return hinted && [...brands].some((brand) => GENERIC_HEIF_BRANDS.has(brand));
}

function assertHeicDimensions(bytes: Uint8Array): void {
  let typeOffset = findType(bytes, 'ispe');
  while (typeOffset >= 0) {
    if (typeOffset >= 4 && typeOffset + 16 <= bytes.length) {
      const boxSize = uint32(bytes, typeOffset - 4);
      if (boxSize >= 20) {
        const widthPx = uint32(bytes, typeOffset + 8);
        const heightPx = uint32(bytes, typeOffset + 12);
        const pixelCount = widthPx * heightPx;
        if (
          widthPx > DEFAULT_ASSET_LIMITS.maxRasterDimensionPx
          || heightPx > DEFAULT_ASSET_LIMITS.maxRasterDimensionPx
          || pixelCount > DEFAULT_ASSET_LIMITS.maxRasterPixels
        ) {
          throw new AssetError('ASSET_PIXEL_LIMIT', 'La foto supera el limite de resolucion.', {
            widthPx,
            heightPx,
            pixelCount,
          });
        }
      }
    }
    typeOffset = findType(bytes, 'ispe', typeOffset + 4);
  }
}

function hasPdfSignature(bytes: Uint8Array): boolean {
  return bytes.length >= 5 && ascii(bytes, 0, 5) === '%PDF-';
}

function outputName(sourceName: string): string {
  const base = sourceName.replace(/\.hei[cf]$/iu, '').trim() || 'foto';
  return `${base}.jpg`;
}

async function convertHeic(file: File): Promise<File> {
  const { heicTo } = await import('heic-to/csp');
  try {
    let output = await heicTo({ blob: file, type: 'image/jpeg', quality: 0.92 });
    if (output.size > DEFAULT_ASSET_LIMITS.maxFileBytes) {
      output = await heicTo({ blob: file, type: 'image/jpeg', quality: 0.82 });
    }
    if (!output.size) throw new Error('El conversor genero un archivo vacio.');
    if (output.size > DEFAULT_ASSET_LIMITS.maxFileBytes) {
      throw new AssetError('ASSET_TOO_LARGE', 'La foto convertida supera el limite de peso.');
    }
    return new File([output], outputName(file.name), {
      type: 'image/jpeg',
      lastModified: file.lastModified,
    });
  } catch (error) {
    if (error instanceof AssetError) throw error;
    throw new AssetError(
      'ASSET_CORRUPT',
      'No pudimos preparar esta foto.',
      { sourceName: file.name },
      { cause: error },
    );
  }
}

export async function normalizeImportedFile(file: File): Promise<NormalizedImportedFile> {
  if (!file.size) throw new AssetError('ASSET_EMPTY', 'El archivo esta vacio.');
  if (file.size > DEFAULT_ASSET_LIMITS.maxFileBytes) {
    throw new AssetError('ASSET_TOO_LARGE', 'El archivo supera el limite de peso.', {
      sizeBytes: file.size,
      maxFileBytes: DEFAULT_ASSET_LIMITS.maxFileBytes,
    });
  }

  const header = new Uint8Array(await file.slice(0, Math.min(file.size, HEADER_BYTES)).arrayBuffer());
  const hintedHeic = HEIC_EXTENSION.test(file.name) || HEIC_MIME.test(file.type);
  const detectedHeic = isHeic(header, hintedHeic);
  if (hintedHeic && !detectedHeic) {
    throw new AssetError('ASSET_CORRUPT', 'La foto HEIC/HEIF no se puede leer.', {
      sourceName: file.name,
    });
  }
  if (detectedHeic) {
    assertHeicDimensions(header);
    return {
      file: await convertHeic(file),
      kind: 'image',
      convertedFromHeic: true,
    };
  }

  const looksLikePdf = hasPdfSignature(header)
    || file.type.toLowerCase() === 'application/pdf'
    || /\.pdf$/iu.test(file.name);
  return {
    file,
    kind: looksLikePdf ? 'pdf' : 'image',
    convertedFromHeic: false,
  };
}
